import { getStripe } from "./stripeClient";
import api from "./axios";
import { StripeLinkedAccountsResponse } from "./types";

const stripeApi = {
    link: async (business_id?: string) => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            // Backend route uses underscore: /stripe/client_secret
            const res = await api.post("/stripe/client_secret", null, config);

            // Stripe.js - auth flow
            const { client_secret } = res.data;
            const stripe = await getStripe();
            if (!stripe) {
                console.error("Stripe failed to initialize.");
                return;
            }
            const result = await stripe.collectFinancialConnectionsAccounts({
                clientSecret: client_secret
            });
            console.log(result.financialConnectionsSession)
            if (result.error) {
                console.error("Stripe error:", result.error.message);
                alert("Linking failed. Please try again.");
                return;
            }
        } catch (err) {
            console.error("Link failed", err);
            alert("Something went wrong while linking your bank.");
        }
    },
    getLinkedAccounts: async (business_id?: string): Promise<StripeLinkedAccountsResponse> => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const res = await api.get("/stripe/linked-accounts", config);
            return res.data;
        } catch (err) {
            console.error("Failed to fetch linked accounts", err);
            throw err;
        }
    },
    disconnect: async (id: string, business_id?: string) => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            await api.delete(`/stripe/linked-accounts/${id}`, config);
        } catch (err) {
            console.error("Failed to disconnect account", err);
            throw err;
        }
    },
    verifyBalance: async (id: string, business_id?: string) => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.post(`/stripe/linked-accounts/${id}/verify-balance`, null, config);
            return response.data;
        } catch (err) {
            console.error("Failed to verify account balance", err);
            throw err;
        }
    },
    syncAccountTransactions: async (id: string, business_id?: string) => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.post(`/stripe/linked-accounts/${id}/sync-transactions`, null, config);
            return response.data;
        } catch (err) {
            console.error("Failed to sync account transactions", err);
            throw err;
        }
    },
    syncAllTransactions: async (business_id?: string) => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.post("/stripe/linked-accounts/sync-transactions", null, config);
            return response.data;
        } catch (err) {
            console.error("Failed to sync all account transactions", err);
            throw err;
        }
    },
    reconnectAccount: async (id: string, business_id?: string) => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.post(`/stripe/linked-accounts/${id}/reconnect`, null, config);
            return response.data;
        } catch (err) {
            console.error("Failed to reconnect account", err);
            throw err;
        }
    },
    manualIngestTransactions: async (
        transactions: Record<string, any>[],
        business_id?: string,
    ) => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.post('/stripe/manual-ingest-transactions', { transactions }, config);
            return response.data;
        } catch (err) {
            console.error('Failed to manually ingest raw bank transactions', err);
            throw err;
        }
    },
    backfillNormalizedFromRaw: async (
        payload: {
            transaction_ids?: string[];
            execute?: boolean;
            statuses?: string[];
            batch_size?: number;
            include_renormalized?: boolean;
            force_historical_ai_resolution?: boolean;
            exclude_checks?: boolean;
        },
        business_id?: string,
    ) => {
        try {
            const config = business_id ? { params: { business_id } } : {};
            const response = await api.post('/stripe/backfill-normalized-from-raw', payload, config);
            return response.data;
        } catch (err) {
            console.error('Failed to replay raw bank transactions into normalization', err);
            throw err;
        }
    }
}

export default stripeApi;
